import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDate, fmtDateTime, money, BILLING_STATUSES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, ClipboardCheck, RefreshCw } from "lucide-react";

const BUCKETS = [
  { key: "missing_cpt", title: "Missing CPT code(s)", hint: "Sent out but nothing to bill against yet.", action: "cpt" },
  { key: "unbilled", title: "Not billed", hint: "CPT assigned or not, these have never been submitted.", action: "billing" },
  { key: "not_charged", title: "Results back, not charged to patient", hint: "Reference lab reported, patient account untouched.", action: "charge" },
  { key: "awaiting_results", title: "Awaiting results", hint: "Aged by days since it left the lab.", action: "results" },
  { key: "materials_outstanding", title: "Materials not returned", hint: "Blocks and slides still sitting at the reference lab.", action: "materials" },
  { key: "denied", title: "Denied claims", hint: "Needs rework or a write-off decision.", action: "billing" },
];

export default function BillingWorklist() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [cpt, setCpt] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/sendouts/worklist");
      setData(res.data);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (row, body, message) => {
    setBusy(row.id);
    try {
      await api.put(`/sendouts/${row.id}`, body);
      toast.success(message);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const saveCpt = (row) => {
    const value = (cpt[row.id] || "").trim();
    if (!value) { toast.error("Enter at least one CPT code"); return; }
    setCpt((c) => ({ ...c, [row.id]: "" }));
    patch(row, { cpt_codes: value }, `CPT saved for ${row.accession_number}`);
  };

  if (!data) return <div className="text-sm text-slate-500" data-testid="worklist-loading">Loading worklist…</div>;
  const s = data.summary;
  const writable = can("sendouts:write");

  const Tile = ({ label, value, sub, tone, testid }) => (
    <div className="border border-slate-200 p-3" data-testid={testid}>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`num mt-1 font-display text-2xl font-bold ${tone === "danger" && value > 0 ? "text-red-800" : "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="worklist-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Billing Worklist</h1>
          <p className="mt-1 text-sm text-slate-600">
            Today's send-out follow-ups · generated {fmtDateTime(data.generated_at)} · results chased after {data.thresholds.results_days} days, materials after {data.thresholds.materials_days}
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="worklist-refresh-button" className="h-11">
          <RefreshCw size={15} className="mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7" data-testid="worklist-summary">
        <Tile label="Missing CPT" value={s.missing_cpt} tone="danger" testid="worklist-missing-cpt" />
        <Tile label="Not billed" value={s.unbilled} sub={`${money(s.open_charges)} open`} tone="danger" testid="worklist-unbilled" />
        <Tile label="Not charged" value={s.not_charged} sub="Results already back" testid="worklist-not-charged" />
        <Tile label="Awaiting results" value={s.awaiting_results} sub={`${s.results_overdue} overdue`} testid="worklist-awaiting-results" />
        <Tile label="Materials out" value={s.materials_outstanding} sub={`${s.materials_overdue} overdue`} testid="worklist-materials-out" />
        <Tile label="Denied" value={s.denied} tone="danger" testid="worklist-denied" />
        <Tile label="Total send-outs" value={s.total_sendouts} testid="worklist-total" />
      </div>

      {BUCKETS.map((b) => {
        const rows = data.buckets[b.key] || [];
        return (
          <div key={b.key} className="border border-slate-200" data-testid={`worklist-bucket-${b.key}`}>
            <div className="flex flex-wrap items-baseline gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-700">{b.title}</span>
              <span className="num rounded bg-white px-1.5 text-xs font-bold text-slate-800">{rows.length}</span>
              <span className="text-xs text-slate-500">{b.hint}</span>
            </div>
            {rows.length === 0 ? (
              <div className="flex items-center gap-2 p-4 text-sm text-green-800" data-testid={`worklist-clear-${b.key}`}>
                <ClipboardCheck size={15} /> Nothing pending — all clear.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px] text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr className="border-b border-slate-200">
                      <th className="px-3 py-2">Case</th><th className="px-3 py-2">Patient</th>
                      <th className="px-3 py-2">Destination / test</th><th className="px-3 py-2">CPT</th>
                      <th className="px-3 py-2">Sent</th><th className="px-3 py-2 text-right">Days out</th>
                      <th className="px-3 py-2">Billing</th><th className="px-3 py-2 text-right">Charge</th>
                      <th className="px-3 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-b border-slate-100 row-hover hover:bg-slate-50" data-testid={`worklist-row-${b.key}-${r.id}`}>
                        <td className="px-3 py-2">
                          <Link to={`/cases/${r.case_id}`} className="font-mono font-semibold text-[#134e4a] hover:underline">{r.accession_number}</Link>
                        </td>
                        <td className="px-3 py-2">{r.patient_name}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-800">{r.destination_lab}</div>
                          <div className="text-xs text-slate-500">{r.test_requested}</div>
                        </td>
                        <td className="px-3 py-2 font-mono text-slate-700">{r.cpt_codes || "—"}</td>
                        <td className="px-3 py-2 num text-slate-600">{fmtDate(r.date_sent)}</td>
                        <td className="px-3 py-2 text-right">
                          <span className={`num font-semibold ${r.results_overdue || r.materials_overdue ? "text-red-800" : "text-slate-700"}`}>
                            {r.days_out ?? "—"}
                          </span>
                          {(r.results_overdue || r.materials_overdue) && (
                            <AlertTriangle size={13} className="ml-1 inline text-red-800" data-testid={`worklist-overdue-${r.id}`} />
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-700">{r.billing_status}</td>
                        <td className="px-3 py-2 num text-right">{money(r.charge_amount)}</td>
                        <td className="px-3 py-2">
                          {!writable ? <span className="text-xs text-slate-400">read only</span> : (
                            <div className="flex items-center justify-end gap-1.5">
                              {b.action === "cpt" && (
                                <>
                                  <Input
                                    value={cpt[r.id] || ""} onChange={(e) => setCpt((c) => ({ ...c, [r.id]: e.target.value }))}
                                    onKeyDown={(e) => e.key === "Enter" && saveCpt(r)}
                                    placeholder="88342, 88341" data-testid={`worklist-cpt-input-${r.id}`}
                                    className="h-9 w-40 border-slate-300 font-mono text-xs"
                                  />
                                  <Button size="sm" disabled={busy === r.id} onClick={() => saveCpt(r)}
                                    data-testid={`worklist-save-cpt-${r.id}`} className="h-9 bg-[#134e4a] hover:bg-[#0f3f3c]">
                                    Save
                                  </Button>
                                </>
                              )}
                              {b.action === "billing" && (
                                <select
                                  value={r.billing_status} data-testid={`worklist-billing-select-${r.id}`}
                                  onChange={(e) => patch(r, { billing_status: e.target.value }, `${r.accession_number} marked ${e.target.value}`)}
                                  className="h-9 rounded-md border border-slate-300 bg-white px-2 text-xs"
                                >
                                  {BILLING_STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
                                </select>
                              )}
                              {b.action === "charge" && (
                                <Button size="sm" variant="outline" disabled={busy === r.id}
                                  onClick={() => patch(r, { charged_to_patient: true }, `${r.accession_number} charged to patient`)}
                                  data-testid={`worklist-mark-charged-${r.id}`} className="h-9">
                                  Mark charged
                                </Button>
                              )}
                              {b.action === "results" && (
                                <Button size="sm" variant="outline" disabled={busy === r.id}
                                  onClick={() => patch(r, { results_received: true }, `Results logged for ${r.accession_number}`)}
                                  data-testid={`worklist-mark-results-${r.id}`} className="h-9">
                                  Results received
                                </Button>
                              )}
                              {b.action === "materials" && (
                                <Button size="sm" variant="outline" disabled={busy === r.id}
                                  onClick={() => patch(r, { materials_returned: true }, `Materials returned for ${r.accession_number}`)}
                                  data-testid={`worklist-mark-materials-${r.id}`} className="h-9">
                                  Materials back
                                </Button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
